import React, { useState } from 'react';
import styled from 'styled-components';

// Types for OMS Trade Proposals
export interface TradeProposal {
  id: string;
  type: 'new_trade' | 'block_trade' | 'rebalance' | 'hedge' | 'tax_harvest' | 'rollover';
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'posted';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  expiresAt?: string;

  // Trade details
  trade: {
    side: 'BUY' | 'SELL';
    security: string;
    cusip: string;
    quantity: number;
    price?: number;
    notional: number;
    portfolioId: string;
    portfolioName: string;
  };

  // Why section
  rationale: {
    summary: string;
    factors: Array<{
      label: string;
      value: string;
      impact: 'positive' | 'negative' | 'neutral';
    }>;
    expectedOutcome: string;
  };

  // Evidence section
  evidence: Array<{
    source: string;
    type: 'market_data' | 'analytics' | 'compliance' | 'research' | 'execution';
    description: string;
    data?: Record<string, any>;
    timestamp: string;
  }>;

  // Risk section
  risk: {
    level: 'low' | 'medium' | 'high';
    factors: Array<{
      category: string;
      description: string;
      severity: 'low' | 'medium' | 'high';
      mitigation?: string;
    }>;
    complianceStatus: 'passed' | 'pending' | 'failed' | 'warning';
    complianceNotes?: string[];
  };

  // Agent assessments
  agentAssessments: Array<{
    agentType: 'RESEARCH' | 'COMPLIANCE' | 'EXECUTION' | 'ORCHESTRATOR';
    decision: 'approve' | 'reject' | 'conditional';
    confidence: number;
    reasoning: string;
    conditions?: string[];
  }>;

  // Rollback capability
  rollback?: {
    available: boolean;
    description?: string;
    reversalTrade?: Partial<TradeProposal['trade']>;
  };

  // Workflow info
  workflowStage: 'DRAFT' | 'PENDING_PRECLEAR' | 'APPROVED' | 'SENT' | 'EXECUTED' | 'POSTED';
  sessionId?: string;
}

interface ProposalCardProps {
  proposal: TradeProposal;
  onApprove?: (proposalId: string) => void;
  onReject?: (proposalId: string, reason: string) => void;
  onModify?: (proposalId: string) => void;
  onExecute?: (proposalId: string) => void;
  onPost?: (proposalId: string) => void;
  expanded?: boolean;
}

const Card = styled.div<{ $priority: string; $status: string }>`
  background: #1a1a2e;
  border-radius: 12px;
  border: 1px solid ${props => {
    if (props.$status === 'rejected') return '#ef4444';
    if (props.$status === 'approved') return '#22c55e';
    if (props.$status === 'executed') return '#3b82f6';
    if (props.$status === 'posted') return '#8b5cf6';
    if (props.$priority === 'urgent') return '#ef4444';
    if (props.$priority === 'high') return '#f59e0b';
    return '#2a2a4a';
  }};
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => {
      if (props.$status === 'rejected') return '#ef4444';
      if (props.$status === 'approved') return '#22c55e';
      return '#3b82f6';
    }};
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.1);
  }
`;

const CardHeader = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #2a2a4a;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const TradeBadge = styled.span<{ $side: 'BUY' | 'SELL' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$side === 'BUY' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  color: ${props => props.$side === 'BUY' ? '#22c55e' : '#ef4444'};
`;

const SecurityName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const TradeDetails = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #9ca3af;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PriorityBadge = styled.span<{ $priority: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$priority) {
      case 'urgent': return 'rgba(239, 68, 68, 0.2)';
      case 'high': return 'rgba(245, 158, 11, 0.2)';
      case 'medium': return 'rgba(59, 130, 246, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      default: return '#9ca3af';
    }
  }};
`;

const WorkflowBadge = styled.span<{ $stage: string }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    switch (props.$stage) {
      case 'DRAFT': return 'rgba(107, 114, 128, 0.2)';
      case 'PENDING_PRECLEAR': return 'rgba(245, 158, 11, 0.2)';
      case 'APPROVED': return 'rgba(34, 197, 94, 0.2)';
      case 'SENT': return 'rgba(59, 130, 246, 0.2)';
      case 'EXECUTED': return 'rgba(139, 92, 246, 0.2)';
      case 'POSTED': return 'rgba(236, 72, 153, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$stage) {
      case 'DRAFT': return '#9ca3af';
      case 'PENDING_PRECLEAR': return '#f59e0b';
      case 'APPROVED': return '#22c55e';
      case 'SENT': return '#3b82f6';
      case 'EXECUTED': return '#8b5cf6';
      case 'POSTED': return '#ec4899';
      default: return '#9ca3af';
    }
  }};
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #2a2a4a;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 16px;
  background: ${props => props.$active ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? '#3b82f6' : '#9ca3af'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.05);
    color: ${props => props.$active ? '#3b82f6' : '#ffffff'};
  }
`;

const TabContent = styled.div`
  padding: 16px;
  max-height: 300px;
  overflow-y: auto;
`;

const RationaleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RationaleSummary = styled.p`
  font-size: 14px;
  color: #e5e7eb;
  line-height: 1.5;
  margin: 0;
`;

const FactorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FactorItem = styled.div<{ $impact: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border-left: 3px solid ${props => {
    switch (props.$impact) {
      case 'positive': return '#22c55e';
      case 'negative': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const FactorLabel = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

const FactorValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
`;

const ExpectedOutcome = styled.div`
  padding: 12px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

const OutcomeLabel = styled.span`
  font-size: 11px;
  color: #3b82f6;
  text-transform: uppercase;
  font-weight: 600;
`;

const OutcomeText = styled.p`
  font-size: 13px;
  color: #e5e7eb;
  margin: 8px 0 0;
  line-height: 1.4;
`;

const EvidenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EvidenceItem = styled.div`
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid #2a2a4a;
`;

const EvidenceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const EvidenceSource = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #3b82f6;
`;

const EvidenceType = styled.span<{ $type: string }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$type) {
      case 'market_data': return 'rgba(34, 197, 94, 0.2)';
      case 'analytics': return 'rgba(59, 130, 246, 0.2)';
      case 'compliance': return 'rgba(139, 92, 246, 0.2)';
      case 'research': return 'rgba(245, 158, 11, 0.2)';
      case 'execution': return 'rgba(236, 72, 153, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'market_data': return '#22c55e';
      case 'analytics': return '#3b82f6';
      case 'compliance': return '#8b5cf6';
      case 'research': return '#f59e0b';
      case 'execution': return '#ec4899';
      default: return '#9ca3af';
    }
  }};
`;

const EvidenceDescription = styled.p`
  font-size: 13px;
  color: #e5e7eb;
  margin: 0;
  line-height: 1.4;
`;

const EvidenceTimestamp = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-top: 8px;
  display: block;
`;

const RiskSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RiskOverview = styled.div<{ $level: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  background: ${props => {
    switch (props.$level) {
      case 'low': return 'rgba(34, 197, 94, 0.1)';
      case 'medium': return 'rgba(245, 158, 11, 0.1)';
      case 'high': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$level) {
      case 'low': return 'rgba(34, 197, 94, 0.3)';
      case 'medium': return 'rgba(245, 158, 11, 0.3)';
      case 'high': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(107, 114, 128, 0.3)';
    }
  }};
`;

const RiskLevel = styled.span<{ $level: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    switch (props.$level) {
      case 'low': return '#22c55e';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#9ca3af';
    }
  }};
`;

const ComplianceBadge = styled.span<{ $status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: auto;
  background: ${props => {
    switch (props.$status) {
      case 'passed': return 'rgba(34, 197, 94, 0.2)';
      case 'pending': return 'rgba(245, 158, 11, 0.2)';
      case 'failed': return 'rgba(239, 68, 68, 0.2)';
      case 'warning': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'passed': return '#22c55e';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'warning': return '#f59e0b';
      default: return '#9ca3af';
    }
  }};
`;

const RiskFactorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RiskFactor = styled.div<{ $severity: string }>`
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border-left: 3px solid ${props => {
    switch (props.$severity) {
      case 'low': return '#22c55e';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const RiskCategory = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  display: block;
  margin-bottom: 4px;
`;

const RiskDescription = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
`;

const RiskMitigation = styled.p`
  font-size: 12px;
  color: #3b82f6;
  margin: 8px 0 0;
  padding-top: 8px;
  border-top: 1px solid #2a2a4a;
`;

const AgentAssessments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #2a2a4a;
`;

const AssessmentTitle = styled.h4`
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  margin: 0 0 8px;
`;

const AssessmentItem = styled.div<{ $decision: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid ${props => {
    switch (props.$decision) {
      case 'approve': return 'rgba(34, 197, 94, 0.3)';
      case 'reject': return 'rgba(239, 68, 68, 0.3)';
      case 'conditional': return 'rgba(245, 158, 11, 0.3)';
      default: return '#2a2a4a';
    }
  }};
`;

const AgentIcon = styled.div<{ $type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: ${props => {
    switch (props.$type) {
      case 'RESEARCH': return 'rgba(59, 130, 246, 0.2)';
      case 'COMPLIANCE': return 'rgba(139, 92, 246, 0.2)';
      case 'EXECUTION': return 'rgba(34, 197, 94, 0.2)';
      case 'ORCHESTRATOR': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
`;

const AssessmentContent = styled.div`
  flex: 1;
`;

const AssessmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const AgentName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const DecisionBadge = styled.span<{ $decision: string }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$decision) {
      case 'approve': return 'rgba(34, 197, 94, 0.2)';
      case 'reject': return 'rgba(239, 68, 68, 0.2)';
      case 'conditional': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$decision) {
      case 'approve': return '#22c55e';
      case 'reject': return '#ef4444';
      case 'conditional': return '#f59e0b';
      default: return '#9ca3af';
    }
  }};
`;

const AssessmentReasoning = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.4;
`;

const ConfidenceBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const ConfidenceTrack = styled.div`
  flex: 1;
  height: 4px;
  background: #2a2a4a;
  border-radius: 2px;
  overflow: hidden;
`;

const ConfidenceFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${props => props.$value * 100}%;
  background: ${props => {
    if (props.$value >= 0.8) return '#22c55e';
    if (props.$value >= 0.6) return '#3b82f6';
    if (props.$value >= 0.4) return '#f59e0b';
    return '#ef4444';
  }};
  border-radius: 2px;
`;

const ConfidenceLabel = styled.span`
  font-size: 11px;
  color: #6b7280;
  min-width: 35px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid #2a2a4a;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' | 'danger' | 'success' }>`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #3b82f6;
          border: none;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'success':
        return `
          background: #22c55e;
          border: none;
          color: white;
          &:hover { background: #16a34a; }
        `;
      case 'danger':
        return `
          background: transparent;
          border: 1px solid #ef4444;
          color: #ef4444;
          &:hover { background: rgba(239, 68, 68, 0.1); }
        `;
      default:
        return `
          background: transparent;
          border: 1px solid #3b82f6;
          color: #3b82f6;
          &:hover { background: rgba(59, 130, 246, 0.1); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RejectDialog = styled.div`
  padding: 16px;
  background: #1a1a2e;
  border-top: 1px solid #2a2a4a;
`;

const RejectInput = styled.textarea`
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid #2a2a4a;
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #6b7280;
  }
`;

const RejectActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatQuantity = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toString();
};

const getAgentIcon = (type: string): string => {
  switch (type) {
    case 'RESEARCH': return '🔬';
    case 'COMPLIANCE': return '⚖️';
    case 'EXECUTION': return '⚡';
    case 'ORCHESTRATOR': return '🎯';
    default: return '🤖';
  }
};

export const ProposalCard: React.FC<ProposalCardProps> = ({
  proposal,
  onApprove,
  onReject,
  onModify,
  onExecute,
  onPost,
  expanded = false,
}) => {
  const [activeTab, setActiveTab] = useState<'why' | 'evidence' | 'risk'>('why');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleReject = () => {
    if (rejectReason.trim() && onReject) {
      onReject(proposal.id, rejectReason);
      setShowRejectDialog(false);
      setRejectReason('');
    }
  };

  const getActionButtons = () => {
    switch (proposal.workflowStage) {
      case 'DRAFT':
        return (
          <>
            <ActionButton $variant="secondary" onClick={onModify ? () => onModify(proposal.id) : undefined}>
              Modify
            </ActionButton>
            <ActionButton $variant="primary" onClick={onApprove ? () => onApprove(proposal.id) : undefined}>
              Submit for Pre-Clear
            </ActionButton>
          </>
        );
      case 'PENDING_PRECLEAR':
        return (
          <>
            <ActionButton $variant="danger" onClick={() => setShowRejectDialog(true)}>
              Reject
            </ActionButton>
            <ActionButton $variant="success" onClick={onApprove ? () => onApprove(proposal.id) : undefined}>
              Approve
            </ActionButton>
          </>
        );
      case 'APPROVED':
        return onExecute ? (
          <ActionButton $variant="primary" onClick={() => onExecute(proposal.id)}>
            Open Trade Ticket
          </ActionButton>
        ) : null;
      case 'EXECUTED':
        return onPost ? (
          <ActionButton $variant="success" onClick={() => onPost(proposal.id)}>
            Post to IBOR
          </ActionButton>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Card $priority={proposal.priority} $status={proposal.status}>
      <CardHeader onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
        <HeaderLeft>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TradeBadge $side={proposal.trade.side}>
              {proposal.trade.side}
            </TradeBadge>
            <SecurityName>{proposal.trade.security}</SecurityName>
          </div>
          <TradeDetails>
            <DetailItem>
              <span style={{ color: '#6b7280' }}>Qty:</span> {formatQuantity(proposal.trade.quantity)}
            </DetailItem>
            <DetailItem>
              <span style={{ color: '#6b7280' }}>Notional:</span> {formatCurrency(proposal.trade.notional)}
            </DetailItem>
            <DetailItem>
              <span style={{ color: '#6b7280' }}>Portfolio:</span> {proposal.trade.portfolioName}
            </DetailItem>
          </TradeDetails>
        </HeaderLeft>
        <HeaderRight>
          <div style={{ display: 'flex', gap: '8px' }}>
            <PriorityBadge $priority={proposal.priority}>{proposal.priority}</PriorityBadge>
            <WorkflowBadge $stage={proposal.workflowStage}>
              {proposal.workflowStage.replace('_', ' ')}
            </WorkflowBadge>
          </div>
        </HeaderRight>
      </CardHeader>

      {isExpanded && (
        <>
          <TabContainer>
            <Tab $active={activeTab === 'why'} onClick={() => setActiveTab('why')}>
              Why
            </Tab>
            <Tab $active={activeTab === 'evidence'} onClick={() => setActiveTab('evidence')}>
              Evidence ({proposal.evidence.length})
            </Tab>
            <Tab $active={activeTab === 'risk'} onClick={() => setActiveTab('risk')}>
              Risk
            </Tab>
          </TabContainer>

          <TabContent>
            {activeTab === 'why' && (
              <RationaleSection>
                <RationaleSummary>{proposal.rationale.summary}</RationaleSummary>

                <FactorsList>
                  {proposal.rationale.factors.map((factor, index) => (
                    <FactorItem key={index} $impact={factor.impact}>
                      <FactorLabel>{factor.label}</FactorLabel>
                      <FactorValue>{factor.value}</FactorValue>
                    </FactorItem>
                  ))}
                </FactorsList>

                <ExpectedOutcome>
                  <OutcomeLabel>Expected Outcome</OutcomeLabel>
                  <OutcomeText>{proposal.rationale.expectedOutcome}</OutcomeText>
                </ExpectedOutcome>

                {proposal.agentAssessments.length > 0 && (
                  <AgentAssessments>
                    <AssessmentTitle>Agent Assessments</AssessmentTitle>
                    {proposal.agentAssessments.map((assessment, index) => (
                      <AssessmentItem key={index} $decision={assessment.decision}>
                        <AgentIcon $type={assessment.agentType}>
                          {getAgentIcon(assessment.agentType)}
                        </AgentIcon>
                        <AssessmentContent>
                          <AssessmentHeader>
                            <AgentName>{assessment.agentType}</AgentName>
                            <DecisionBadge $decision={assessment.decision}>
                              {assessment.decision}
                            </DecisionBadge>
                          </AssessmentHeader>
                          <AssessmentReasoning>{assessment.reasoning}</AssessmentReasoning>
                          <ConfidenceBar>
                            <ConfidenceTrack>
                              <ConfidenceFill $value={assessment.confidence} />
                            </ConfidenceTrack>
                            <ConfidenceLabel>{Math.round(assessment.confidence * 100)}%</ConfidenceLabel>
                          </ConfidenceBar>
                        </AssessmentContent>
                      </AssessmentItem>
                    ))}
                  </AgentAssessments>
                )}
              </RationaleSection>
            )}

            {activeTab === 'evidence' && (
              <EvidenceList>
                {proposal.evidence.map((item, index) => (
                  <EvidenceItem key={index}>
                    <EvidenceHeader>
                      <EvidenceSource>{item.source}</EvidenceSource>
                      <EvidenceType $type={item.type}>{item.type.replace('_', ' ')}</EvidenceType>
                    </EvidenceHeader>
                    <EvidenceDescription>{item.description}</EvidenceDescription>
                    <EvidenceTimestamp>
                      {new Date(item.timestamp).toLocaleString()}
                    </EvidenceTimestamp>
                  </EvidenceItem>
                ))}
              </EvidenceList>
            )}

            {activeTab === 'risk' && (
              <RiskSection>
                <RiskOverview $level={proposal.risk.level}>
                  <RiskLevel $level={proposal.risk.level}>
                    Risk Level: {proposal.risk.level.toUpperCase()}
                  </RiskLevel>
                  <ComplianceBadge $status={proposal.risk.complianceStatus}>
                    Compliance: {proposal.risk.complianceStatus}
                  </ComplianceBadge>
                </RiskOverview>

                {proposal.risk.complianceNotes && proposal.risk.complianceNotes.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {proposal.risk.complianceNotes.map((note, i) => (
                      <p key={i} style={{ margin: '4px 0' }}>• {note}</p>
                    ))}
                  </div>
                )}

                <RiskFactorsList>
                  {proposal.risk.factors.map((factor, index) => (
                    <RiskFactor key={index} $severity={factor.severity}>
                      <RiskCategory>{factor.category}</RiskCategory>
                      <RiskDescription>{factor.description}</RiskDescription>
                      {factor.mitigation && (
                        <RiskMitigation>Mitigation: {factor.mitigation}</RiskMitigation>
                      )}
                    </RiskFactor>
                  ))}
                </RiskFactorsList>

                {proposal.rollback?.available && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 600 }}>
                      ↩️ Rollback Available
                    </span>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>
                      {proposal.rollback.description || 'This trade can be reversed if needed'}
                    </p>
                  </div>
                )}
              </RiskSection>
            )}
          </TabContent>

          {showRejectDialog ? (
            <RejectDialog>
              <RejectInput
                placeholder="Enter reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                autoFocus
              />
              <RejectActions>
                <ActionButton
                  $variant="secondary"
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectReason('');
                  }}
                  style={{ flex: 'none', padding: '8px 16px' }}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  $variant="danger"
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  style={{ flex: 'none', padding: '8px 16px' }}
                >
                  Confirm Rejection
                </ActionButton>
              </RejectActions>
            </RejectDialog>
          ) : (
            <ActionBar>
              {getActionButtons()}
            </ActionBar>
          )}
        </>
      )}
    </Card>
  );
};

export default ProposalCard;
